import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { eventService } from '../../services/eventService';
import './UpcomingEvents.css';

const UpcomingEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [imageErrors, setImageErrors] = useState({});
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const getFullImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    
    if (imageUrl.startsWith('/nlnapi/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/uploads/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    
    if (imageUrl.startsWith('/')) {
      return `https://nlngraclub.com${imageUrl}`;
    }
    
    return `https://nlngraclub.com/${imageUrl}`;
  };

  const handleImageError = (eventId) => {
    setImageErrors(prev => ({ ...prev, [eventId]: true }));
  };

  const handleViewDetails = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setTimeout(() => setSelectedEvent(null), 300);
  };

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        console.log('Fetching events from API...');
        const response = await eventService.getEvents(true, 50);
        console.log('API Response:', response);
        
        if (response && response.success && response.events && response.events.length > 0) {
          const processedEvents = response.events.map(event => ({
            id: event.id,
            title: event.title || '',
            description: event.description || '',
            image_url: event.image_url || null,
            event_date: event.event_date,
            event_time: event.event_time,
            location: event.location || '',
            event_type: event.event_type || '',
            price: event.price || 'Free for members',
            capacity: event.max_attendees ? `${event.max_attendees} people` : 'Limited slots',
            status: event.event_date >= new Date().toISOString().split('T')[0] ? 'upcoming' : 'past'
          }));
          setEvents(processedEvents);
        } else {
          console.log('No events found in API response');
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchEvents();
  }, []);

  const categories = ['all', ...new Set(events.map(event => event.event_type).filter(Boolean))];
  
  const filteredEvents = selectedCategory === 'all' 
    ? events 
    : events.filter(event => event.event_type === selectedCategory);

  const formatEventDate = (dateString) => {
    if (!dateString) return 'Date TBA';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Date TBA';
      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (e) {
      return 'Date TBA';
    }
  };

  const formatEventTime = (timeString) => {
    if (!timeString) return 'Time TBA';
    return timeString;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-hero">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="eyebrow">Events & Activities</span>
              <h1>Upcoming Events</h1>
              <p className="section-sub">Discover exciting events and activities happening at the club</p>
            </motion.div>
          </div>
        </div>
        <section className="section">
          <div className="container">
            <div className="loading-spinner" style={{ textAlign: 'center', padding: '60px 20px' }}>
              <i className="fas fa-spinner fa-spin" style={{ fontSize: '48px', color: '#2e7d32' }}></i>
              <p style={{ marginTop: '20px' }}>Loading events...</p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="eyebrow">Events & Activities</span>
            <h1>Upcoming Events</h1>
            <p className="section-sub">Discover exciting events and activities happening at the club</p>
          </motion.div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {categories.length > 1 && (
            <div className="event-filters">
              {categories.map(cat => (
                <motion.button
                  key={cat}
                  className={`filter-btn ${selectedCategory === cat ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(cat)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {cat === 'all' ? 'All Events' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </motion.button>
              ))}
            </div>
          )}

          {events.length === 0 ? (
            <motion.div 
              className="empty-state" 
              style={{ textAlign: 'center', padding: '60px 20px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <i className="fas fa-calendar-times" style={{ fontSize: '48px', color: '#999', marginBottom: '20px' }}></i>
              <p>No upcoming events at this time.</p>
              <p style={{ fontSize: '14px', color: '#666' }}>Check back soon for new events!</p>
            </motion.div>
          ) : (
            <div className="events-grid">
              {filteredEvents.map((event, index) => (
                <motion.div
                  key={event.id}
                  className="event-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <div className="event-image">
                    {event.image_url && !imageErrors[event.id] ? (
                      <img 
                        src={getFullImageUrl(event.image_url)} 
                        alt={event.title}
                        onError={() => handleImageError(event.id)}
                      />
                    ) : (
                      <div className="no-image">
                        <i className="fas fa-calendar-alt"></i>
                        <span>No Image</span>
                      </div>
                    )}
                    <div className="event-status">{event.status === 'upcoming' ? 'Upcoming' : 'Past Event'}</div>
                  </div>
                  <div className="event-content">
                    {event.event_type && <div className="event-category">{event.event_type}</div>}
                    <h3>{event.title}</h3>
                    <div className="event-details">
                      <div className="event-detail">
                        <i className="fas fa-calendar-alt"></i>
                        <span>{formatEventDate(event.event_date)}</span>
                      </div>
                      <div className="event-detail">
                        <i className="fas fa-clock"></i>
                        <span>{formatEventTime(event.event_time)}</span>
                      </div>
                      <div className="event-detail">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{event.location || 'Location TBA'}</span>
                      </div>
                    </div>
                    <p className="event-description">
                      {event.description && event.description.length > 120 
                        ? `${event.description.substring(0, 120)}...` 
                        : event.description || 'No description available'}
                    </p>
                    <div className="event-footer">
                      <motion.button 
                        className="btn btn-outline btn-view"
                        onClick={() => handleViewDetails(event)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        View Details <i className="fas fa-arrow-right"></i>
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

    {/* Event Modal - Professional Layout with Auto Height Images */}
<AnimatePresence>
  {showModal && selectedEvent && (
    <motion.div 
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={closeModal}
    >
      <motion.div 
        className="event-detail-modal"
        initial={{ scale: 0.95, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={closeModal}>
          <i className="fas fa-times"></i>
        </button>
        
        <div className="modal-layout">
          {/* Image Section - Auto Height */}
          <div className="modal-image-section">
            {selectedEvent.image_url && !imageErrors[selectedEvent.id] ? (
              <img 
                src={getFullImageUrl(selectedEvent.image_url)} 
                alt={selectedEvent.title}
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  display: 'block',
                  objectFit: 'contain'
                }}
                onError={() => handleImageError(selectedEvent.id)}
              />
            ) : (
              <div className="modal-no-image">
                <i className="fas fa-calendar-alt"></i>
                <span>No Image Available</span>
              </div>
            )}
          </div>
          
          {/* Content Section */}
          <div className="modal-content-section">
            <div className="modal-header">
              {selectedEvent.event_type && (
                <div className="modal-category">
                  <i className="fas fa-tag"></i>
                  <span>{selectedEvent.event_type}</span>
                </div>
              )}
              <h2>{selectedEvent.title}</h2>
            </div>
            
            {/* Info Grid */}
            <div className="modal-info-grid">
              <div className="modal-info-card">
                <div className="modal-info-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                <div className="modal-info-text">
                  <label>Date</label>
                  <span>{formatEventDate(selectedEvent.event_date)}</span>
                </div>
              </div>
              
              <div className="modal-info-card">
                <div className="modal-info-icon">
                  <i className="fas fa-clock"></i>
                </div>
                <div className="modal-info-text">
                  <label>Time</label>
                  <span>{formatEventTime(selectedEvent.event_time)}</span>
                </div>
              </div>
              
              <div className="modal-info-card">
                <div className="modal-info-icon">
                  <i className="fas fa-map-marker-alt"></i>
                </div>
                <div className="modal-info-text">
                  <label>Location</label>
                  <span>{selectedEvent.location || 'Location TBA'}</span>
                </div>
              </div>
              
              <div className="modal-info-card">
                <div className="modal-info-icon">
                  <i className="fas fa-ticket-alt"></i>
                </div>
                <div className="modal-info-text">
                  <label>Price</label>
                  <span>{selectedEvent.price}</span>
                </div>
              </div>
            </div>
            
            {/* Description */}
            <div className="modal-description">
              <h4>
                <i className="fas fa-info-circle"></i>
                About This Event
              </h4>
              <p>{selectedEvent.description || 'No description available.'}</p>
            </div>
            
            {/* Footer */}
            <div className="modal-footer">
              <button className="btn-primary" onClick={closeModal}>
                Close <i className="fas fa-check"></i>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>
    </div>
  );
};

export default UpcomingEvents;